"use client";

import React, { useState, useEffect } from "react";
import Image from "next/image";
import { Copy, Check, Loader2, MoreHorizontal, Pencil, Plus } from "lucide-react";
import { getAttributeRecord, getAttributeString } from "@/lib/utils/dynamodb";
import { ReportDisplayProps, ReportData, MeetingData, StructureDefinition, StructuredDefinition } from "./types";
import { RebuildCustomPrompt } from "./RebuildCustomPrompt";
import { ExportOptions } from "./PDFExport";
import { DynamicField } from "./DynamicField";
import { getTranscribedText, formatDate, copyToClipboard, DEFAULT_STRUCTURE_DEFINITION, parseStructureDefinition, getStructureDefinitionId, getFieldOrder, getStructureDefinitionIdFromProcessingStatus } from "./utils";
import { getStructuredDefinitions } from "@/lib/clients/structured-definitions";
import { useMonoXOneSubmission } from "@/hooks/useMonoXOneSubmission";
import { getMonoXOneConfig } from "@/lib/clients/structured-info";
import type { MonoXOneConfigForDB, MonoXOneMapping } from "@/types/structured-info";
import { toast } from "sonner";
import { MonoXOneSubmissionResult } from "./MonoXOneSubmissionResult";
import { ReportHeaderTags } from "./ReportHeaderTags";
import TranscriptionCorrectionModal from "./TranscriptionCorrectionModal";
import { upsertDictionary } from "@/lib/clients/dictionary";
import type { DictionaryEntry } from "@/types/dictionary";

export const ReportDisplay: React.FC<ReportDisplayProps> = ({ 
  processingStatus, 
  rebuildReport, 
  isRebuilding = false 
}) => {
  console.log('🎯 [ReportDisplay] コンポーネントがレンダリングされました！');
  console.log('🎯 [ReportDisplay] processingStatus:', processingStatus);
  console.log('🎯 [ReportDisplay] states:', processingStatus?.states);
  console.log('🎯 [ReportDisplay] report存在:', !!processingStatus?.report);
  const [isTranscriptOpen, setIsTranscriptOpen] = useState(false);
  const [isEditing, setIsEditing] = useState(false);
  const [editedData, setEditedData] = useState<MeetingData>({});
  const [lastSavedData, setLastSavedData] = useState<MeetingData>({});
  const [structureDefinition, setStructureDefinition] = useState<StructureDefinition>(DEFAULT_STRUCTURE_DEFINITION);
  const [structuredDefinitions, setStructuredDefinitions] = useState<StructuredDefinition[]>([]);
  const reportRef = React.useRef<HTMLDivElement>(null);
  const [isVisible, setIsVisible] = useState(false);
  const [copied, setCopied] = useState<string | null>(null);
  const [monoXOneConfig, setMonoXOneConfig] = useState<MonoXOneConfigForDB | MonoXOneMapping | null>(null);
  // 文字起こし選択 → 辞書追加
  const transcriptContentRef = React.useRef<HTMLDivElement | null>(null);
  const [transcriptSelectionText, setTranscriptSelectionText] = useState("");
  const [isCorrectionOpen, setIsCorrectionOpen] = useState(false);
  
  // アクションメニューの状態管理
  const [isActionMenuOpen, setIsActionMenuOpen] = useState(false);

  // MONO-X One送信用のフック
  const {
    isSubmitting: isSubmittingToMonoX,
    result: submissionResult,
    currentStage,
    submitToMonoXOne,
    clearResult
  } = useMonoXOneSubmission();

  useEffect(() => {
    const timer = setTimeout(() => {
      setIsVisible(true);
    }, 100);
    
    return () => clearTimeout(timer);
  }, []);

  // 構造定義一覧を取得
  useEffect(() => {
    const loadStructuredDefinitions = async () => {
      try {
        const definitions = await getStructuredDefinitions();
        setStructuredDefinitions(definitions);
      } catch (error) {
        console.error("構造定義一覧の取得に失敗:", error);
      }
    };

    loadStructuredDefinitions();
  }, []);

  // MONO-X One連携設定を取得
  useEffect(() => {
    const loadMonoXOneConfig = async () => {
      const reportData = processingStatus as unknown as ReportData;
      let structureId = getStructureDefinitionId(reportData);
      
      // レポートデータからstructure_definition_idが取得できない場合、
      // ローカルストレージから最後に使用したIDを取得
      if (!structureId) {
        const lastUsedStructureId = localStorage.getItem('lastUsedStructureDefinitionId');
        structureId = lastUsedStructureId;
      }
      
      if (!structureId) {
        return;
      }
      
      try {
        const config = await getMonoXOneConfig(structureId);
        console.log('🔍 MONO-X One設定取得結果:', {
          structureId,
          hasConfig: !!config,
          configType: config ? ('targetSelection' in config ? 'new' : 'legacy') : 'none'
        });
        setMonoXOneConfig(config);
      } catch (error) {
        console.error('MONO-X One設定の取得に失敗:', error);
      }
    };
    
    loadMonoXOneConfig();
  }, [processingStatus]);

  // 構造定義を取得・設定
  useEffect(() => {
    if (processingStatus?.report) {
      const structureId = getStructureDefinitionId(processingStatus as unknown as ReportData);
      
      // まず、カスタム構造定義IDがあるかチェック
      if (structureId && structuredDefinitions.length > 0) {
        const definition = structuredDefinitions.find(def => def.id === structureId);
        if (definition) {
          try {
            const parsedDefinition = parseStructureDefinition(definition.json);
            setStructureDefinition(parsedDefinition);
            if (process.env.NODE_ENV === 'development') {
              console.log("ReportDisplay: カスタム構造定義を適用", structureId, parsedDefinition);
            }
            return; // カスタム構造定義が見つかった場合は、自動検出をスキップ
          } catch (error) {
            console.error("構造定義の解析に失敗:", error);
          }
        }
      }
      
      if (!structureId && processingStatus.report?.meeting_data) {
        const meetingDataRecord = getAttributeRecord(processingStatus.report.meeting_data);

        if (meetingDataRecord) {
          try {
            const actualStructure: StructureDefinition = {};
            Object.keys(meetingDataRecord).forEach(key => {
              if (meetingDataRecord[key] && typeof meetingDataRecord[key] === 'string') {
                actualStructure[key] = {
                  type: "str",
                  description: key.charAt(0).toUpperCase() + key.slice(1).replace(/_/g, ' ')
                };
              }
            });

            if (Object.keys(actualStructure).length > 0) {
              if (process.env.NODE_ENV === 'development') {
                console.log('🔍 生成された構造定義:', actualStructure);
              }

              setStructureDefinition(actualStructure);
              if (process.env.NODE_ENV === 'development') {
                console.log("ReportDisplay: 実際の構造を使用", Object.keys(actualStructure));
              }
              return;
            }
          } catch (error) {
            console.error("meeting_dataからの構造生成に失敗:", error);
          }
        }
      }
      
      // 全て失敗した場合はデフォルトを使用
      setStructureDefinition(DEFAULT_STRUCTURE_DEFINITION);
    }
  }, [processingStatus, structuredDefinitions]);

  // APIレスポンスからの更新時に再レンダリングするために追加
  useEffect(() => {
    if (!processingStatus?.report?.meeting_data) {
      return;
    }

    const meetingDataRecord = getAttributeRecord(processingStatus.report.meeting_data);

    if (!meetingDataRecord) {
      return;
    }

    try {
      const cleanedMeetingData = { ...meetingDataRecord };
      Object.keys(cleanedMeetingData).forEach(key => {
        if (typeof cleanedMeetingData[key] === 'string') {
          cleanedMeetingData[key] = (cleanedMeetingData[key] as string)
            .replace(/^```markdown\n/g, '')
            .replace(/\n```$/g, '');
        }
      });

      setEditedData(cleanedMeetingData);
      setLastSavedData(cleanedMeetingData);

      if (process.env.NODE_ENV === 'development') {
        console.log("ReportDisplay: データ更新完了（markdownコードブロック除去済み）");
      }
    } catch (e) {
      console.error("meeting_dataのパースに失敗:", e);
    }
  }, [processingStatus]);

  // ここで早期returnはしない（hooksの順序を保つため）。返却直前で判定。

  // APIレスポンスからレポートデータを抽出
  const reportData = processingStatus as unknown as ReportData;
  const transcriptText = getTranscribedText(reportData);
  const employeeIdentifier = getAttributeString(reportData.report?.pk) || reportData.employeeID || processingStatus?.employeeID || processingStatus?.userId || "";
  const reportIdentifier = getAttributeString(reportData.report?.sk) || reportData.reportID || processingStatus?.reportID || processingStatus?.reportId || "";
  
  // 開発環境でのみ重要な情報のみをログ出力
  if (process.env.NODE_ENV === 'development' && monoXOneConfig) {
    console.log("MONO-X One設定が利用可能です");
  }

  // 文字起こしデータトグル処理
  const toggleTranscript = () => {
    setIsTranscriptOpen(!isTranscriptOpen);
  };

  // 文字起こし内の選択監視
  useEffect(() => {
    if (!isTranscriptOpen) {
      setTranscriptSelectionText("");
      return;
    }
    const handleSelection = () => {
      if (isCorrectionOpen) return; // モーダル中は保持
      try {
        const sel = window.getSelection();
        const text = sel?.toString()?.trim() || "";
        if (!text) {
          setTranscriptSelectionText("");
          return;
        }
        const container = transcriptContentRef.current;
        const anchorNode = sel?.anchorNode as Node | null;
        const focusNode = sel?.focusNode as Node | null;
        if (container && anchorNode && focusNode && container.contains(anchorNode) && container.contains(focusNode)) {
          // 選択されたテキストからタイムスタンプを除去
          const cleanText = text
            .replace(/\[\d{2}:\d{2}\]/g, '') // タイムスタンプを除去
            .replace(/\s+/g, ' ') // 複数の空白を単一に
            .trim(); // 前後の空白を除去
          setTranscriptSelectionText(cleanText);
        } else {
          setTranscriptSelectionText("");
        }
      } catch {
        setTranscriptSelectionText("");
      }
    };
    document.addEventListener("selectionchange", handleSelection);
    return () => document.removeEventListener("selectionchange", handleSelection);
  }, [isTranscriptOpen, isCorrectionOpen]);

  const openCorrectionFromSelection = () => {
    setIsCorrectionOpen(true);
  };

  // 編集モードの切り替え
  const toggleEditMode = () => {
    if (isEditing) {
      // 変更を破棄する前に確認
      if (confirm('変更を破棄してもよろしいですか？')) {
        // 編集をキャンセルし、前回保存したデータに戻す
        setEditedData(JSON.parse(JSON.stringify(lastSavedData)));
        setIsEditing(false);
      }
      // キャンセルした場合は編集モードを継続
      return;
    }
    setIsEditing(!isEditing);
  };

  // 変更を保存する処理
  const saveChanges = () => {
    // PDFオブジェクトが混入している可能性があるため、純粋なデータだけを抽出
    try {
      const cleanData = JSON.parse(JSON.stringify(editedData));
      setEditedData(cleanData);
      // 保存したデータを記録
      setLastSavedData(cleanData);
      console.log("保存するデータ:", cleanData);
      alert('変更が保存されました');
    } catch (e) {
      console.error("データの保存に失敗:", e);
      alert('データの保存に失敗しました');
    }
    setIsEditing(false);
  };

  // フォーム入力の処理
  const handleInputChange = (field: string, value: unknown) => {
    // 文字列の場合はmarkdownコードブロックを除去
    let cleanedValue = value;
    if (typeof value === 'string') {
      cleanedValue = value
        .replace(/^```markdown\n/g, '') // 先頭の```markdownを除去
        .replace(/\n```$/g, ''); // 末尾の```を除去
    }
    
    setEditedData((prev: MeetingData) => ({
      ...prev,
      [field]: cleanedValue
    }));
  };

  // 配列アイテムの変更処理
  const handleArrayItemChange = (field: string, index: number, value: string) => {
    setEditedData((prev: MeetingData) => {
      const newArray = [...(prev[field] as string[] || [])];
      newArray[index] = value;
      return {
        ...prev,
        [field]: newArray
      };
    });
  };

  // 配列アイテムの追加
  const handleAddArrayItem = (field: string) => {
    setEditedData((prev: MeetingData) => ({
      ...prev,
      [field]: [...(prev[field] as string[] || []), ""]
    }));
  };

  // 配列アイテムの削除
  const handleRemoveArrayItem = (field: string, index: number) => {
    setEditedData((prev: MeetingData) => {
      const newArray = [...(prev[field] as string[] || [])];
      newArray.splice(index, 1);
      return {
        ...prev,
        [field]: newArray
      };
    });
  };

  // カスタムプロンプトの送信
  const handleSubmitPrompt = (customPrompt: string) => {
    if (!rebuildReport) {
      console.error("rebuildReport関数が提供されていません");
      return;
    }

    const employeeID = employeeIdentifier;
    const reportID = reportIdentifier;

    if (!employeeID || !reportID) {
      alert("レポート情報が不足しています");
      return;
    }

    rebuildReport(employeeID, reportID, customPrompt);
  };

  // MONO-X One送信ハンドラー
  const handleMonoXOneSubmission = async () => {
    console.log('🔥 MONO-X One送信ボタンがクリックされました');
    console.log('🔥 現在の状態:', {
      hasConfig: !!monoXOneConfig,
      hasData: !!editedData,
      isSubmitting: isSubmittingToMonoX,
      currentStage
    });
    
    // レポートデータの確認
    if (!editedData) {
      console.error('🔥 レポートデータが不足');
      toast.error('送信に失敗しました', {
        description: 'レポートデータが見つかりません。ページを再読み込みしてください。'
      });
      return;
    }
    
    // MONO-X One連携設定の確認
    if (!monoXOneConfig) {
      console.error('🔥 MONO-X One連携設定が未設定');
      toast.error('MONO-X One連携設定が未設定です', {
        description: '設定画面で構造化情報にMONO-X One連携設定を行ってください。'
      });
      return;
    }
    
    try {
      console.log('🚀 MONO-X One送信開始');
      
      // Markdown形式のレポートデータを生成
      const markdownReportData = getMarkdownReportForMonoXOne();
      
      // 🔍 デバッグ: MONO-X One設定の詳細確認
      console.log('🔍 [MONO-X Debug] MONO-X One設定詳細:', {
        configType: 'targetSelection' in monoXOneConfig ? 'new' : 'legacy',
        tenantName: monoXOneConfig.tenantName,
        フィールドマッピング数: monoXOneConfig.fieldMappings?.length || 0,
        フィールドマッピング詳細: monoXOneConfig.fieldMappings?.map(mapping => ({
          structuredFieldPath: mapping.structuredFieldPath,
          monoXInputId: mapping.monoXInputId,
          monoXInputLabel: mapping.monoXInputLabel,
          実際のデータ存在: markdownReportData.hasOwnProperty(mapping.structuredFieldPath),
          実際のデータ値: markdownReportData[mapping.structuredFieldPath] 
            ? String(markdownReportData[mapping.structuredFieldPath]).substring(0, 100) + '...'
            : 'undefined/null'
        }))
      });
      
      console.log('🚀 送信データ:', {
        configType: 'targetSelection' in monoXOneConfig ? 'new' : 'legacy',
        tenantName: monoXOneConfig.tenantName,
        dataKeys: Object.keys(markdownReportData),
        送信データ概要: Object.keys(markdownReportData).length + '個のフィールド'
      });
      
      const result = await submitToMonoXOne(monoXOneConfig, markdownReportData);
      
      console.log('🚀 送信結果:', result);
      
      // 送信成功時の処理
      if (result.success && result.url) {
        if (result.verified) {
          toast.success('MONO-X Oneに送信完了！', {
            description: `データが正常に反映されました（${result.retryCount}回目の確認で成功）`,
            action: {
              label: '公開ページを確認',
              onClick: () => window.open(result.url, '_blank', 'noopener,noreferrer')
            }
          });
        } else {
          toast.warning('送信完了（反映状況要確認）', {
            description: 'データ送信は完了しましたが、反映状況を手動で確認してください',
            action: {
              label: '公開ページを確認',
              onClick: () => window.open(result.url, '_blank', 'noopener,noreferrer')
            }
          });
        }
      } else {
        // 送信失敗時の詳細なエラー処理
        const errorMessage = result.error || '不明なエラーが発生しました';
        console.error('🚀 送信失敗:', errorMessage);
        
        // エラーメッセージに応じて適切なtoastを表示
        if (errorMessage.includes('MONO-X One接続設定が見つかりません')) {
          toast.error('認証情報が見つかりません', {
            description: '設定画面でMONO-X One接続設定（ユーザー名・パスワード）を行ってください。'
          });
        } else if (errorMessage.includes('MONO-X One認証情報が不完全')) {
          toast.error('認証情報が不完全です', {
            description: '設定画面でMONO-X One接続設定のユーザー名・パスワードを正しく入力してください。'
          });
        } else if (errorMessage.includes('50008') || errorMessage.includes('認証エラー')) {
          toast.error('MONO-X One認証に失敗しました', {
            description: '認証情報が正しくありません。設定画面で認証情報を確認してください。'
          });
        } else if (errorMessage.includes('送信可能なデータが見つかりません')) {
          // より詳細なエラー情報を提供
          const missingFieldsMatch = errorMessage.match(/以下のフィールドを確認してください: (.+?)(\n|$)/);
          const availableFieldsMatch = errorMessage.match(/利用可能なフィールド: (.+?)(\n|$)/);
          const missingFields = missingFieldsMatch ? missingFieldsMatch[1] : '不明';
          const availableFields = availableFieldsMatch ? availableFieldsMatch[1] : '';
          
          toast.error('フィールドマッピングエラー', {
            description: `必要フィールド: ${missingFields}${availableFields ? `\n利用可能: ${availableFields.substring(0, 50)}...` : ''}`,
            action: {
              label: '詳細をコンソールで確認',
              onClick: () => {
                console.group('🔍 [MONO-X Debug] フィールドマッピングエラー詳細');
                console.log('不足フィールド:', missingFields);
                console.log('利用可能フィールド:', availableFields);
                console.log('完全なエラーメッセージ:', errorMessage);
                console.groupEnd();
              }
            }
          });
        } else if (errorMessage.includes('50001') || errorMessage.includes('パラメータエラー')) {
          toast.error('MONO-X One設定エラー', {
            description: 'MONO-X One連携設定が正しくありません。設定画面で連携設定を確認してください。'
          });
        } else if (errorMessage.includes('50002') || errorMessage.includes('権限エラー')) {
          toast.error('MONO-X One権限エラー', {
            description: 'MONO-X One側で適切な権限が設定されていません。管理者に確認してください。'
          });
        } else if (errorMessage.includes('50003') || errorMessage.includes('データ不正エラー')) {
          toast.error('データ形式エラー', {
            description: 'MONO-X One側のフォーム要件に合わないデータが含まれています。必須項目や文字数制限を確認してください。'
          });
        } else {
          toast.error('送信に失敗しました', {
            description: errorMessage
          });
        }
      }
      
    } catch (error) {
      console.error('🚀 MONO-X One送信エラー:', error);
      const errorMessage = error instanceof Error ? error.message : '不明なエラーが発生しました';
      
      // catchブロックでの詳細なエラー処理
      if (errorMessage.includes('MONO-X One接続設定が見つかりません')) {
        toast.error('認証情報が見つかりません', {
          description: '設定画面でMONO-X One接続設定（ユーザー名・パスワード）を行ってください。'
        });
      } else if (errorMessage.includes('MONO-X One認証情報が不完全')) {
        toast.error('認証情報が不完全です', {
          description: '設定画面でMONO-X One接続設定のユーザー名・パスワードを正しく入力してください。'
        });
      } else if (errorMessage.includes('50008') || errorMessage.includes('認証エラー')) {
        toast.error('MONO-X One認証に失敗しました', {
          description: '認証情報が正しくありません。設定画面で認証情報を確認してください。'
        });
      } else {
        // 汎用エラー処理（詳細情報付き）
        const isNetworkError = errorMessage.includes('Network') || errorMessage.includes('fetch');
        const isTimeoutError = errorMessage.includes('timeout');
        const isDataError = errorMessage.includes('送信可能なデータが見つかりません');
        
        let title = '送信処理でエラーが発生しました';
        let description = errorMessage;
        
        if (isNetworkError) {
          title = 'ネットワークエラー';
          description = 'MONO-X Oneへの接続に失敗しました。ネットワーク状況を確認してください。';
        } else if (isTimeoutError) {
          title = 'タイムアウトエラー';
          description = '処理時間が長すぎます。しばらく待ってから再試行してください。';
        } else if (isDataError) {
          title = 'データ不足エラー';
          description = 'レポートデータが不完全です。';
        }
        
        toast.error(title, {
          description: description.length > 100 ? description.substring(0, 100) + '...' : description,
          action: {
            label: 'エラー詳細',
            onClick: () => {
              console.group('🔍 [MONO-X Debug] キャッチされたエラー詳細');
              console.log('エラーオブジェクト:', error);
              console.log('エラーメッセージ:', errorMessage);
              console.log('エラータイプ:', typeof error);
              console.log('スタックトレース:', error instanceof Error ? error.stack : 'なし');
              console.groupEnd();
              
              // ユーザー向けの詳細表示
              alert(`エラー詳細:\n\n${errorMessage}\n\n詳細はコンソールをご確認ください。`);
            }
          }
        });
      }
    }
  };
  
  // セクションをコピーする関数
  const handleCopySection = async (text: string, sectionName: string) => {
    // デバッグログを追加
    if (process.env.NODE_ENV === 'development') {
      console.log(`🔍 コピー対象セクション: ${sectionName}`);
      console.log(`🔍 コピー対象テキスト長: ${text.length}`);
      console.log(`🔍 コピー対象テキスト内容:`, text);
    }
    
    if (!text || text.trim() === '') {
      console.error('❌ コピー対象のテキストが空です');
      alert('コピー対象のデータが見つかりません。ページを再読み込みしてください。');
      return;
    }
    
    const success = await copyToClipboard(text);
    if (success) {
      if (process.env.NODE_ENV === 'development') {
        console.log('✅ コピー成功');
      }
      setCopied(sectionName);
      // 2秒後にコピー状態をリセット
      setTimeout(() => {
        setCopied(null);
      }, 2000);
    } else {
      console.error('❌ コピー失敗');
      alert('コピーに失敗しました。ブラウザがクリップボードアクセスを許可していることを確認してください。');
    }
  };

  // 動的な全レポートテキストを生成
  const getFullReportText = (): string => {
    if (process.env.NODE_ENV === 'development') {
      console.log('🔍 getFullReportText 実行開始');
      console.log('🔍 editedData:', editedData);
      console.log('🔍 structureDefinition:', structureDefinition);
    }
    
    // Contentフィールドがある場合は、その内容のみを返す（タイトルも含まない）
    if (contentFieldKey && editedData[contentFieldKey] && typeof editedData[contentFieldKey] === 'string') {
      const contentValue = editedData[contentFieldKey];
      // 「営業日報」と「Content」を除去
      const cleanedContent = contentValue
        .split('\n')
        .filter(line => {
          const trimmedLine = line.trim();
          return trimmedLine !== '営業日報' && 
                 trimmedLine !== 'Content' && 
                 trimmedLine !== 'content' &&
                 trimmedLine !== '';
        })
        .join('\n');
      return cleanedContent;
    }
    
    // 全体要約フィールドを最初に表示
    const summaryFields = ['全体要約', 'summary', 'meeting_summary', 'overview'];
    const allFields = Object.keys(structureDefinition);
    
    // 全体要約フィールドを最初に配置
    const summaryExisting = summaryFields.filter(field => allFields.includes(field));
    
    // 基本情報フィールド
    const basicFields = ['meeting_purpose', 'client', 'datetime'];
    const basicExisting = basicFields.filter(field => allFields.includes(field));
    
    // 残りのフィールド
    const remainingFields = allFields.filter(field => 
      !summaryFields.includes(field) && 
      !basicFields.includes(field)
    );
    
    const orderedFields = [...summaryExisting, ...basicExisting, ...remainingFields];
    
    if (process.env.NODE_ENV === 'development') {
      console.log('🔍 orderedFields:', orderedFields);
    }
    
    let reportText = "";

    // タイトル用のフィールドを探す（「営業日報」をデフォルトにしない）
    const titleField = editedData.meeting_purpose || editedData.title || getReportTitle();
    if (titleField && titleField !== "営業日報") {
      reportText += `${titleField}\n\n`;
    }
    if (process.env.NODE_ENV === 'development') {
      console.log('🔍 タイトル追加:', titleField);
    }

    orderedFields.forEach(fieldName => {
      // Contentフィールドは既に処理済みなのでスキップ
      if (fieldName === 'Content' || fieldName === 'content') return;
      
      const fieldDef = structureDefinition[fieldName];
      const value = editedData[fieldName];
      
      if (process.env.NODE_ENV === 'development') {
        console.log(`🔍 フィールド処理: ${fieldName}`, {
          fieldDef,
          value,
          hasValue: !!value,
          hasFieldDef: !!fieldDef
        });
      }
      
      if (value && fieldDef) {
        // 「Content」という説明は表示しない
        if (fieldDef.description && fieldDef.description !== 'Content') {
          reportText += `${fieldDef.description}\n`;
        }
        
        if (Array.isArray(value)) {
          if (fieldName === 'participants') {
            value.forEach(participant => {
              reportText += `- ${participant}\n`;
            });
          } else {
            value.forEach(item => {
              reportText += `- ${item}\n`;
            });
          }
        } else if (fieldName === 'datetime') {
          reportText += `${formatDate(value as string)}\n`;
        } else {
          // 不要なmarkdownコードブロックを除去
          const cleanedValue = String(value)
            .replace(/^```markdown\n/g, '') // 先頭の```markdownを除去
            .replace(/\n```$/g, ''); // 末尾の```を除去
          reportText += `${cleanedValue}\n`;
        }
        reportText += `\n`;
      }
    });

    // editedDataから追加フィールドも含める（構造定義にない場合）
    Object.keys(editedData).forEach(key => {
      if (!orderedFields.includes(key) && 
          !['meeting_purpose', 'title'].includes(key) && 
          editedData[key] && 
          typeof editedData[key] === 'string' &&
          (editedData[key] as string).trim() !== '') {
        const description = key.charAt(0).toUpperCase() + key.slice(1).replace(/_/g, ' ');
        reportText += `${description}\n${editedData[key]}\n\n`;
        if (process.env.NODE_ENV === 'development') {
          console.log(`🔍 追加フィールド: ${key} = ${editedData[key]}`);
        }
      }
    });

    if (process.env.NODE_ENV === 'development') {
      console.log('🔍 最終レポートテキスト長:', reportText.length);
      console.log('🔍 最終レポートテキスト:', reportText);
    }
    
    // 不要なmarkdownコードブロックを除去
    const cleanedReportText = reportText
      .replace(/^```markdown\n/g, '') // 先頭の```markdownを除去
      .replace(/\n```$/g, ''); // 末尾の```を除去
    
    return cleanedReportText;
  };

  // MONO-X One送信用のMarkdown形式レポート生成
  const getMarkdownReportForMonoXOne = (): Record<string, unknown> => {
    const markdownReport = getFullReportText();
    
    // 元の editedData を基に、全体のMarkdownテキストを含むオブジェクトを作成
    const reportData = {
      ...editedData,
      // 全体のMarkdownテキストを追加
      fullReport: markdownReport,
      // 主要なテキストフィールドもMarkdown形式に変換
      content: markdownReport
    };
    
    // 🔍 デバッグ: 送信データの詳細を出力
    console.log('🔍 [MONO-X Debug] getMarkdownReportForMonoXOne 結果:', {
      利用可能フィールド数: Object.keys(reportData).length,
      利用可能フィールド名: Object.keys(reportData),
      editedDataの元データ: Object.keys(editedData),
      各フィールドの値: Object.fromEntries(
        Object.keys(reportData).map(key => [
          key, 
          typeof (reportData as Record<string, unknown>)[key] === 'string' 
            ? `"${String((reportData as Record<string, unknown>)[key]).substring(0, 50)}${String((reportData as Record<string, unknown>)[key]).length > 50 ? '...' : ''}"`
            : (reportData as Record<string, unknown>)[key]
        ])
      )
    });
    
    return reportData;
  };

  // カスタム構造定義が使用されているかどうかを判定
  const structureId = getStructureDefinitionIdFromProcessingStatus(processingStatus);
  const isCustomStructure = Boolean(structureId);

  // 構造定義に含まれるフィールドのみを表示するためのフィルター関数
  const shouldDisplayField = (fieldName: string) => {
    if (isCustomStructure) {
      return fieldName in structureDefinition;
    }
    return true;
  };

  // フィールド表示順序を取得
  const fieldOrder = getFieldOrder(structureDefinition);
  
  if (process.env.NODE_ENV === 'development') {
    console.log('🔍 fieldOrder生成:', {
      fieldOrder,
      structureDefinitionKeys: Object.keys(structureDefinition),
      fieldOrderLength: fieldOrder.length,
      structureDefinitionLength: Object.keys(structureDefinition).length
    });
  }
  
  // 基本情報フィールドを分離
  const basicInfoFields = ['client', 'datetime'].filter(field => 
    field in structureDefinition && shouldDisplayField(field)
  );
  
  // 参加者フィールド（空の時は非表示）
  const participantsField = fieldOrder.includes('participants') && 
    shouldDisplayField('participants') && 
    editedData.participants && 
    (Array.isArray(editedData.participants) ? editedData.participants.length > 0 : editedData.participants !== '') 
    ? 'participants' : null;
  
  // 全体要約フィールドを最初に表示
  const summaryFields = ['全体要約', 'summary', 'meeting_summary', 'overview'];
  const summaryFieldsInOrder = fieldOrder.filter(field => 
    summaryFields.includes(field) && shouldDisplayField(field)
  );
  
  if (process.env.NODE_ENV === 'development') {
    console.log('🔍 fieldOrder詳細:', {
      fieldOrder,
      summaryFieldsInOrder,
      structureDefinitionKeys: Object.keys(structureDefinition)
    });
  }
  
  // デバッグ用：実際のフィールド名を確認
  if (process.env.NODE_ENV === 'development') {
    console.log('🔍 フィールド順序デバッグ:', {
      fieldOrder,
      summaryFields,
      summaryFieldsInOrder,
      editedDataKeys: Object.keys(editedData),
      structureDefinitionKeys: Object.keys(structureDefinition),
      editedDataValues: Object.entries(editedData).filter(([key, value]) => 
        ['全体要約', 'summary', 'meeting_summary', 'overview'].includes(key) && value
      )
    });
  }
  
  // Contentフィールドがある場合は、その内容のみを表示（大文字小文字両方チェック）
  const contentFieldKey = editedData['Content'] ? 'Content' : editedData['content'] ? 'content' : null;
  const hasContentField = contentFieldKey && typeof editedData[contentFieldKey] === 'string' && editedData[contentFieldKey].trim();
  
  // その他のコンテンツフィールド
  const contentFields = hasContentField 
    ? [contentFieldKey] // Contentフィールドがある場合はそれのみを表示
    : fieldOrder.filter(field => 
        !['meeting_purpose', 'client', 'datetime', 'participants'].includes(field) &&
        !summaryFields.includes(field) &&
        shouldDisplayField(field)
      );

  // デバッグ情報を出力
  if (process.env.NODE_ENV === 'development') {
    console.log('🔍 contentFieldKey:', contentFieldKey);
    console.log('🔍 hasContentField:', hasContentField);
    console.log('🔍 editedData keys:', Object.keys(editedData));
    console.log('🔍 editedData Content:', editedData['Content']);
    console.log('🔍 editedData content:', editedData['content']);
    console.log('🔍 contentFields:', contentFields);
  }

  // 構造定義の名前を取得してタイトルとして使用
  const getReportTitle = (): string => {
    // Contentフィールドがある場合は、その中から最初の見出しまたは行を抽出
    if (contentFieldKey && editedData[contentFieldKey] && typeof editedData[contentFieldKey] === 'string') {
      const contentValue = editedData[contentFieldKey];
      const lines = contentValue.split('\n');
      for (const line of lines) {
        const trimmedLine = line.trim();
        if (trimmedLine.startsWith('# ')) {
          return trimmedLine.substring(2).trim();
        } else if (trimmedLine && !trimmedLine.startsWith('##') && !trimmedLine.startsWith('###') && 
                   trimmedLine !== '営業日報' && trimmedLine !== 'Content' && trimmedLine !== 'content') {
          return trimmedLine;
        }
      }
    }
    
    // 使用された構造定義のIDを取得
    const structureDefinitionId = getStructureDefinitionIdFromProcessingStatus(processingStatus);

    // 構造定義IDが存在する場合、対応する構造定義の名前を取得
    if (structureDefinitionId && Array.isArray(structuredDefinitions)) {
      const usedStructure = structuredDefinitions.find(def => def.id === structureDefinitionId);
      if (usedStructure && usedStructure.name) {
        return usedStructure.name;
      }
    }

    // 構造定義の名前が取得できない場合は従来のロジック（「営業日報」をデフォルトにしない）
    return (editedData.title as string) || (editedData.meeting_purpose as string) || "";
  };

  if (!processingStatus || processingStatus.states !== "reqCompleted") {
    return null;
  }

  return (
    <div 
      ref={reportRef} 
      className={`glass-card max-h-[80vh] w-full max-w-4xl flex flex-col transition-all duration-500 z-50 ${isVisible ? 'opacity-100 transform-none bg-white/90 dark:bg-gray-900/90 backdrop-blur-md' : 'opacity-0 translate-y-4'}`}
      style={{
        backdropFilter: 'blur(10px)',
        WebkitBackdropFilter: 'blur(10px)',
        borderRadius: '0.75rem',
        border: '1px solid rgba(255, 255, 255, 0.125)',
        boxShadow: '0 8px 32px 0 rgba(31, 38, 135, 0.1)'
      }}
    >
      {/* ヘッダー部分 - 固定表示 */}
      <div className="flex-shrink-0 p-6 md:p-8 pb-4 border-b border-gray-200 dark:border-gray-800">
        <div className="flex flex-row items-start justify-between gap-4">
          <div className="flex-grow space-y-3">
            {isEditing ? (
              <input
                type="text"
                value={(editedData.title as string) || ""}
                onChange={(e) => handleInputChange('title', e.target.value)}
                className="text-2xl font-bold bg-transparent border-b-2 border-gray-300 focus:outline-none focus:border-blue-500 w-full p-2"
                placeholder="タイトル"
              />
            ) : (
              <h1 className="text-2xl font-bold text-gray-900 dark:text-gray-100">
                {getReportTitle()}
              </h1>
            )}
            
            {/* レポートヘッダータグの表示 */}
            <ReportHeaderTags 
              reportData={{
                userId: processingStatus?.userId,
                reportId: processingStatus?.reportId,
                employeeID: processingStatus?.employeeID,
                reportID: processingStatus?.reportID,
                states: processingStatus?.states,
                structure_definition_id: processingStatus?.structure_definition_id,
                report: processingStatus?.report as ReportData['report']
              }}
              className="mt-2"
            />
          </div>
          <div className="flex items-center gap-3">
            <div className="flex items-center gap-4">
              {/* MONO-X One送信ボタン - lg以上のみ表示 */}
              {monoXOneConfig ? (
                <button
                  onClick={handleMonoXOneSubmission}
                  disabled={isSubmittingToMonoX || !editedData}
                  className={`hidden lg:inline-flex px-4 py-2 rounded-md cursor-pointer h-[40px] whitespace-nowrap items-center gap-2 ${
                    isSubmittingToMonoX || !editedData
                      ? "bg-gray-400/70 text-gray-500 cursor-not-allowed"
                      : "bg-black text-white hover:bg-gradient-to-r hover:from-black hover:to-yellow-400"
                  } text-sm font-medium transition-all`}
                  aria-label="MONO-X Oneに送信"
                >
                  {isSubmittingToMonoX ? (
                    <span className="flex items-center gap-2">
                      <Loader2 className="w-4 h-4 animate-spin" />
                      送信中...
                    </span>
                  ) : (
                    <span className="flex items-center gap-2">
                      <Image
                        src="/assets/monox-one_icon.webp"
                        alt="MONO-X One"
                        width={16}
                        height={16}
                        className="w-4 h-4"
                      />
                      <span>MONO-X Oneに送信</span>
                    </span>
                  )}
                </button>
              ) : (
                <button
                  disabled
                  className="hidden lg:inline-flex px-4 py-2 rounded-md cursor-not-allowed h-[40px] whitespace-nowrap bg-gray-300/50 text-gray-500 text-sm font-medium"
                  title="MONO-X One設定が必要です"
                >
                  MONO-X One未設定
                </button>
              )}

              {/* カスタムプロンプトフォーム */}
              {rebuildReport && (
                <span className="relative h-[40px] w-[40px]">
                  <RebuildCustomPrompt
                    onSubmit={handleSubmitPrompt}
                    isRebuilding={isRebuilding}
                  />
                </span>
              )}

              {/* アクションメニューボタン */}
              <div className="relative action-menu-container">
                <button
                  onClick={() => setIsActionMenuOpen(!isActionMenuOpen)}
                  className="px-3 py-2 rounded-md cursor-pointer h-[40px] w-[40px] bg-white/70 dark:bg-gray-800/70 text-gray-700 dark:text-gray-300 border border-gray-300/50 dark:border-gray-600/50 backdrop-blur-sm transition-all hover:bg-white/90 dark:hover:bg-gray-800/90 flex items-center justify-center"
                  aria-label="アクションメニュー"
                >
                  <MoreHorizontal className="w-4 h-4" />
                </button>
                
                {/* アクションメニューのドロップダウン */}
                {isActionMenuOpen && (
                  <div className="absolute right-0 top-12 z-50 w-64 bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-lg shadow-lg backdrop-blur-sm">
                    <div className="p-2 space-y-1">
                      {/* MONO-X One送信（lg未満の画面向け） */}
                      {monoXOneConfig ? (
                        <button
                          onClick={async () => {
                            await handleMonoXOneSubmission();
                            setIsActionMenuOpen(false);
                          }}
                          disabled={isSubmittingToMonoX || !editedData}
                          className={`lg:hidden w-full text-left px-3 py-2 text-sm rounded-md flex items-center gap-2 ${
                            isSubmittingToMonoX || !editedData
                              ? 'text-gray-400 cursor-not-allowed'
                              : 'text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700'
                          }`}
                          aria-label="MONO-X Oneに送信"
                        >
                          {isSubmittingToMonoX ? (
                            <>
                              <Loader2 className="h-4 w-4 animate-spin" />
                              <span>送信中...</span>
                            </>
                          ) : (
                            <>
                              <Image 
                                src="/assets/monox-one_icon.webp" 
                                alt="MONO-X One" 
                                width={16}
                                height={16}
                                className="w-4 h-4" 
                              />
                              <span>MONO-X Oneに送信</span>
                            </>
                          )}
                        </button>
                      ) : (
                        <button
                          disabled
                          className="lg:hidden w-full text-left px-3 py-2 text-sm text-gray-400 cursor-not-allowed rounded-md"
                          title="MONO-X One設定が必要です"
                        >
                          MONO-X One未設定
                        </button>
                      )}
                      <div className="lg:hidden border-t border-gray-200 dark:border-gray-600 my-1" />
                      {/* 編集系操作 */}
                      {!isEditing ? (
                        <button
                          onClick={() => {
                            toggleEditMode();
                            setIsActionMenuOpen(false);
                          }}
                          className="w-full text-left px-3 py-2 text-sm text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-md flex items-center gap-2"
                          aria-label="編集"
                        >
                          <Pencil className="h-4 w-4" />
                          <span>編集</span>
                        </button>
                      ) : (
                        <>
                          <button
                            onClick={() => {
                              saveChanges();
                              setIsActionMenuOpen(false);
                            }}
                            className="w-full text-left px-3 py-2 text-sm text-blue-700 dark:text-blue-300 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-md"
                            aria-label="保存"
                          >
                            保存
                          </button>
                          <button
                            onClick={() => {
                              toggleEditMode();
                              setIsActionMenuOpen(false);
                            }}
                            className="w-full text-left px-3 py-2 text-sm text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-md"
                            aria-label="変更を破棄"
                          >
                            変更を破棄
                          </button>
                          <div className="border-t border-gray-200 dark:border-gray-600 my-1" />
                        </>
                      )}
                      
                      <div className="border-t border-gray-200 dark:border-gray-600 my-1" />
                      
                      {/* レポート全体をコピー */}
                      <button
                        onClick={() => {
                          handleCopySection(getFullReportText(), 'fullReport');
                          setIsActionMenuOpen(false);
                        }}
                        className="w-full text-left px-3 py-2 text-sm text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-md flex items-center gap-2"
                        aria-label="レポート全体をコピー"
                      >
                        {copied === 'fullReport' ? (
                          <>
                            <Check className="h-4 w-4 text-green-500" />
                            <span>コピーしました！</span>
                          </>
                        ) : (
                          <>
                            <Copy className="h-4 w-4" />
                            <span>レポート全体をコピー</span>
                          </>
                        )}
                      </button>
                      
                      {/* 文字起こしをコピー */}
                      <button
                        onClick={() => {
                          handleCopySection(getTranscribedText(reportData), 'transcription');
                          setIsActionMenuOpen(false);
                        }}
                        className="w-full text-left px-3 py-2 text-sm text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-md flex items-center gap-2"
                        aria-label="文字起こしをコピー"
                      >
                        {copied === 'transcription' ? (
                          <>
                            <Check className="h-4 w-4 text-green-500" />
                            <span>コピーしました！</span>
                          </>
                        ) : (
                          <>
                            <Copy className="h-4 w-4" />
                            <span>文字起こしをコピー</span>
                          </>
                        )}
                      </button>
                      
                      <div className="border-t border-gray-200 dark:border-gray-600 my-1" />
                      
                      {/* エクスポート機能 */}
                      <div className="px-3 py-2">
                        <ExportOptions 
                          reportData={reportData} 
                          meetingData={editedData} 
                          structureDefinition={structureDefinition}
                        />
                      </div>
                    </div>
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
      </div>
      
      {/* 送信結果の表示 - toastに変更 */}
      {submissionResult && (
        <MonoXOneSubmissionResult
          result={submissionResult}
          onClose={clearResult}
        />
      )}
      
      {/* コンテンツ部分 - スクロール可能 */}
      <div className="flex-1 overflow-y-auto p-6 md:p-8 pt-8">
        {/* 基本情報セクション - 動的対応 */}
        {basicInfoFields.length > 0 && (
          <div className={`mb-8 ${isVisible ? 'opacity-100' : 'opacity-0'} transition-opacity duration-500`}>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              {basicInfoFields.map(fieldName => (
                <DynamicField
                  key={fieldName}
                  fieldName={fieldName}
                  fieldDef={structureDefinition[fieldName]}
                  value={editedData[fieldName]}
                  isEditing={isEditing}
                  copied={copied}
                  onInputChange={handleInputChange}
                  onArrayItemChange={handleArrayItemChange}
                  onAddArrayItem={handleAddArrayItem}
                  onRemoveArrayItem={handleRemoveArrayItem}
                  onCopySection={handleCopySection}
                />
              ))}
            </div>
          </div>
        )}
        
        {/* 全体要約セクション - 最初に表示 */}
        {summaryFieldsInOrder.filter(fieldName => {
          const hasValue = editedData[fieldName];
          if (process.env.NODE_ENV === 'development') {
            console.log(`🔍 全体要約フィールド ${fieldName}:`, hasValue);
          }
          return hasValue;
        }).filter((fieldName, index, array) => {
          // 重複を防ぐ：同じフィールド名が既に表示されている場合は除外
          const firstIndex = array.findIndex(f => f === fieldName);
          return index === firstIndex;
        }).map(fieldName => (
          <DynamicField
            key={fieldName}
            fieldName={fieldName}
            fieldDef={structureDefinition[fieldName]}
            value={editedData[fieldName]}
            isEditing={isEditing}
            copied={copied}
            onInputChange={handleInputChange}
            onArrayItemChange={handleArrayItemChange}
            onAddArrayItem={handleAddArrayItem}
            onRemoveArrayItem={handleRemoveArrayItem}
            onCopySection={handleCopySection}
          />
        ))}
        
        {/* 参加者セクション - 動的対応 */}
        {participantsField && (
          <DynamicField
            fieldName={participantsField}
            fieldDef={structureDefinition[participantsField]}
            value={editedData[participantsField]}
            isEditing={isEditing}
            copied={copied}
            onInputChange={handleInputChange}
            onArrayItemChange={handleArrayItemChange}
            onAddArrayItem={handleAddArrayItem}
            onRemoveArrayItem={handleRemoveArrayItem}
            onCopySection={handleCopySection}
          />
        )}
        
        {/* コンテンツフィールド - 動的対応 */}
        {contentFields.filter(fieldName => {
          const hasValue = fieldName !== 'title' && editedData[fieldName];
          if (process.env.NODE_ENV === 'development') {
            console.log(`🔍 コンテンツフィールドフィルタ: ${fieldName}`, {
              hasValue,
              value: editedData[fieldName],
              type: typeof editedData[fieldName]
            });
          }
          return hasValue;
        }).map(fieldName => (
          <DynamicField
            key={fieldName}
            fieldName={fieldName}
            fieldDef={structureDefinition[fieldName]}
            value={editedData[fieldName]}
            isEditing={isEditing}
            copied={copied}
            onInputChange={handleInputChange}
            onArrayItemChange={handleArrayItemChange}
            onAddArrayItem={handleAddArrayItem}
            onRemoveArrayItem={handleRemoveArrayItem}
            onCopySection={handleCopySection}
          />
        ))}
        
        {/* データに存在するが構造定義にないフィールドも表示 - 全体要約を最初に表示 */}
        {Object.keys(editedData).filter(key => {
          const isSummaryField = ['全体要約', 'summary', 'meeting_summary', 'overview'].includes(key);
          const isBasicField = ['client', 'datetime', 'participants', 'title'].includes(key);
          const isInFieldOrder = fieldOrder.includes(key);
          const isInStructureDefinition = Object.keys(structureDefinition).includes(key);
          const hasValue = editedData[key];
          
          if (process.env.NODE_ENV === 'development') {
            console.log(`🔍 追加フィールド処理: ${key}`, {
              isSummaryField,
              isBasicField,
              isInFieldOrder,
              isInStructureDefinition,
              hasValue,
              shouldDisplay: shouldDisplayField(key)
            });
          }
          
          // 構造定義に含まれるフィールドは追加フィールドとして表示しない
          if (isInStructureDefinition) {
            return false;
          }
          
          // その他のフィールドは構造定義にない場合のみ表示
          return !isBasicField && shouldDisplayField(key) && hasValue;
        }).sort((a, b) => {
          // 全体要約フィールドを最初に配置
          const aIsSummary = ['全体要約', 'summary', 'meeting_summary', 'overview'].includes(a);
          const bIsSummary = ['全体要約', 'summary', 'meeting_summary', 'overview'].includes(b);
          
          if (aIsSummary && !bIsSummary) return -1;
          if (!aIsSummary && bIsSummary) return 1;
          return 0;
        }).filter((fieldName, index, array) => {
          // 重複を防ぐ：同じフィールド名が既に表示されている場合は除外
          const isSummaryField = ['全体要約', 'summary', 'meeting_summary', 'overview'].includes(fieldName);
          if (isSummaryField) {
            // 全体要約フィールドの場合、最初の出現のみを表示
            const firstIndex = array.findIndex(f => f === fieldName);
            return index === firstIndex;
          }
          return true;
        }).map(fieldName => (
          <DynamicField
            key={fieldName}
            fieldName={fieldName}
            fieldDef={{
              type: "str",
              description: fieldName.charAt(0).toUpperCase() + fieldName.slice(1).replace(/_/g, ' ')
            }}
            value={editedData[fieldName]}
            isEditing={isEditing}
            copied={copied}
            onInputChange={handleInputChange}
            onArrayItemChange={handleArrayItemChange}
            onAddArrayItem={handleAddArrayItem}
            onRemoveArrayItem={handleRemoveArrayItem}
            onCopySection={handleCopySection}
          />
        ))}
        
        {/* 音声データのトグル表示 */}
        {transcriptText && (
          <div className="mb-8 mt-6 border border-gray-200/30 dark:border-gray-700/30 rounded-md overflow-hidden transition-opacity duration-500 backdrop-blur-sm bg-white/30 dark:bg-gray-800/30">
            <button 
              onClick={toggleTranscript}
              className="w-full p-4 text-left flex items-center justify-between gap-2 hover:bg-white/50 dark:hover:bg-gray-700/50 transition-colors"
              aria-expanded={isTranscriptOpen}
              aria-label={isTranscriptOpen ? "閉じる" : "開く"}
            >
              <span className="text-lg font-semibold text-gray-900 dark:text-gray-100">音声文字起こしデータ</span>
              <span className="text-gray-500 dark:text-gray-400">
                {isTranscriptOpen ? "閉じる" : "開く"}
              </span>
            </button>
            
            {isTranscriptOpen && (
              <div className="overflow-hidden">
                <div className="relative">
                  {/* 常時ツールバー */}
                  <div className="flex items-center justify-between gap-2 px-3 py-2 border-b bg-white/70 dark:bg-gray-900/40 border-gray-200/60 dark:border-gray-700/60">
                    <div className="text-sm text-gray-600 dark:text-gray-300">
                      {transcriptSelectionText
                        ? `選択中: ${transcriptSelectionText.slice(0, 40)}${transcriptSelectionText.length > 40 ? '…' : ''}`
                        : 'テキストを選択するとボタンが有効になります'}
                    </div>
                    <button
                      type="button"
                      disabled={!transcriptSelectionText}
                      onMouseDown={(e) => { e.preventDefault(); openCorrectionFromSelection(); }}
                      onClick={() => openCorrectionFromSelection()}
                      className={`text-xs flex items-center gap-1 px-2 py-1 rounded border ${transcriptSelectionText ? 'text-blue-600 hover:text-blue-800 dark:text-blue-400 dark:hover:text-blue-300 border-blue-300' : 'text-gray-400 border-gray-300 cursor-not-allowed'}`}
                      title={transcriptSelectionText ? '選択を修正/辞書に追加' : 'テキストを選択してください'}
                    >
                      <Plus className="h-3 w-3" /> 修正/辞書に追加
                    </button>
                  </div>

                  <div
                    ref={transcriptContentRef}
                    className="p-4 bg-gray-100 dark:bg-gray-900/20 overflow-auto max-h-96 text-base whitespace-pre-wrap text-gray-600 dark:text-gray-400"
                  >
                    {transcriptText}
                  </div>
                </div>
              </div>
            )}
          </div>
        )}
        
        {/* フッター部分 - シンプル化（主要機能はヘッダーメニューに移動） */}
        <div className="mt-12 pt-4 border-t border-gray-200/30 dark:border-gray-700/30 flex flex-col md:flex-row justify-between items-start md:items-center gap-4 transition-opacity duration-500">
          <p className="text-sm text-gray-500 dark:text-gray-400">
            レポートID: {employeeIdentifier}-{reportIdentifier}
          </p>
          {/* モバイル表示用に簡易エクスポートを残す */}
          <div className="md:hidden">
            <ExportOptions reportData={reportData} meetingData={editedData} structureDefinition={structureDefinition} />
          </div>
        </div>
      </div>
        {/* 修正モーダル */}
        <TranscriptionCorrectionModal
          isOpen={isCorrectionOpen}
          selectedText={transcriptSelectionText}
          onClose={() => setIsCorrectionOpen(false)}
          onApply={async ({ correctedText }) => {
            if (transcriptSelectionText && correctedText) {
              try {
                await upsertDictionary({
                  original_word: correctedText,
                  incorrect_variants: [transcriptSelectionText],
                  category: "general",
                  is_active: true,
                  source: "manual",
                } as unknown as DictionaryEntry);
                
                // 成功メッセージを表示
                toast.success(`「${transcriptSelectionText}」→「${correctedText}」を辞書に追加しました！`);
              } catch (error) {
                console.error('辞書登録エラー:', error);
                toast.error('辞書への追加に失敗しました。');
              }
            }
            // 辞書登録のみで、構造化結果にはtranscriptionフィールドを追加しない
          }}
        />
    </div>
  );
}; 

export default ReportDisplay;
